import { z } from "zod";
import {
  EXPORT_SCHEMA,
  EXPORT_VERSION,
  MAX_COMMITMENT_YEARS,
  MAX_COMPARE_SCENARIOS,
  MAX_HORIZON_YEARS,
  MAX_PACKAGES,
  MIN_COMMITMENT_YEARS,
  MIN_COMPARE_SCENARIOS,
  MIN_HORIZON_YEARS,
  MIN_PACKAGES,
  PACKAGE_COLOR_SLOTS,
} from "../constants.js";
import {
  BILLING_MODELS,
  CHURN_MODES,
  LINE_BASES,
  LINE_DIRECTIONS,
  LINE_GROUPS,
  MAINTENANCE_MODES,
  RECURRING_PERIODS,
} from "../roi/types.js";
import type { BillingModel, MaintenanceMode, ScenarioModel } from "../roi/types.js";
import {
  booleanQuerySchema,
  countSchema,
  currencyCodeSchema,
  idSchema,
  isoDateTimeSchema,
  moneySchema,
  percentSchema,
  searchQuerySchema,
  unitsSchema,
  userRefSchema,
} from "./common.js";

/**
 * The scenario contract. Every rule in docs/06-api.md#validation-rules lives
 * here, in src/shared, for one reason: the editor must be able to show the exact
 * error the API would return without asking the API. A rule enforced only on the
 * server is a rule the user discovers by losing a save.
 *
 * Two layers, because they earn different status codes:
 *
 *  - `scenarioModelSchema` — structural. A violation is a malformed request and
 *    returns `400 VALIDATION_FAILED` with the offending path.
 *  - `checkModelSemantics` — structurally valid but nonsensical. These return
 *    `409 DUPLICATE_PACKAGE_NAME` or `422 MODEL_INVALID`, and the editor renders
 *    them as blocking warnings against the field that caused them.
 *
 * They are separate passes rather than one `superRefine` so the route can map
 * each to its own code, and so the editor can run the semantic pass on every
 * keystroke without paying for a full parse.
 */

/* --------------------------------------------------------------- the model */

export const visibilitySchema = z.enum(["PRIVATE", "SHARED", "PRODUCT"]);
export type VisibilityName = z.infer<typeof visibilitySchema>;

export const sharePermissionSchema = z.enum(["VIEW", "EDIT"]);
export type SharePermissionName = z.infer<typeof sharePermissionSchema>;

export const billingModelSchema = z.enum(BILLING_MODELS);
export const recurringPeriodSchema = z.enum(RECURRING_PERIODS);
export const maintenanceModeSchema = z.enum(MAINTENANCE_MODES);
export const lineDirectionSchema = z.enum(LINE_DIRECTIONS);
export const lineGroupSchema = z.enum(LINE_GROUPS);
export const lineBasisSchema = z.enum(LINE_BASES);
export const churnModeSchema = z.enum(CHURN_MODES);

export const packageInputSchema = z.object({
  id: idSchema,
  name: z.string().trim().min(1, "Give the package a name.").max(60),
  sortOrder: z.number().int().min(0).max(MAX_PACKAGES),
  colorIndex: z
    .number()
    .int()
    .min(0)
    .max(PACKAGE_COLOR_SLOTS - 1),

  billingModel: billingModelSchema,
  recurringPrice: moneySchema,
  recurringCost: moneySchema,
  recurringPeriod: recurringPeriodSchema,
  oneTimePrice: moneySchema,
  oneTimeCost: moneySchema,
  assetCost: moneySchema,

  maintenanceMode: maintenanceModeSchema,
  maintenanceRatePct: percentSchema,
  maintenanceFlatAmount: moneySchema,
  // Year 1 or year 2 only: support is either bundled into the licence for the
  // first year or it is not, and no contract in the wild starts it later.
  maintenanceStartYear: z.union([z.literal(1), z.literal(2)]),
  maintenanceAttachPct: percentSchema,
});

export const lineItemInputSchema = z.object({
  id: idSchema,
  name: z.string().trim().min(1, "Give the line a name.").max(80),
  direction: lineDirectionSchema,
  group: lineGroupSchema,
  basis: lineBasisSchema,
  // A percentage when `basis` is PCT_OF_REVENUE or PCT_OF_COST, money otherwise.
  // The cross-check is in the model refinement below, where the basis is visible.
  amount: moneySchema,
  unitsPerClient: unitsSchema,
  attachPct: percentSchema,
  startYear: z.number().int().min(1).max(MAX_HORIZON_YEARS),
  endYear: z.number().int().min(1).max(MAX_HORIZON_YEARS).nullable(),
  packageIds: z.array(idSchema).max(MAX_PACKAGES),
  note: z.string().trim().max(200).optional(),
  sortOrder: z.number().int().min(0),
});

export const yearInputSchema = z.object({
  /**
   * Redundant with the array position, which is what the engine reads, and
   * accepted anyway: docs/06-api.md shows it on the wire, it makes a payload
   * readable in a log, and checking it against the index catches a client that
   * reordered the array by mistake.
   */
  yearIndex: z.number().int().min(1).max(MAX_HORIZON_YEARS).optional(),
  newClients: countSchema,
  mix: z.record(countSchema),
  churnMode: churnModeSchema,
  churnCount: countSchema,
  churnRatePct: percentSchema,
  note: z.string().trim().max(200).optional(),
});

const modelShape = z.object({
  /**
   * Read-only and derived from the product, so the server overwrites whatever
   * arrives here. Accepted because the same schema parses a `localStorage`
   * mirror and an exported document, both of which carry it.
   */
  currencyCode: currencyCodeSchema.optional(),
  horizonYears: z.number().int().min(MIN_HORIZON_YEARS).max(MAX_HORIZON_YEARS),
  commitmentYears: z.number().int().min(MIN_COMMITMENT_YEARS).max(MAX_COMMITMENT_YEARS),
  discountRatePct: percentSchema,
  reuseAssets: z.boolean(),
  packages: z.array(packageInputSchema).min(MIN_PACKAGES).max(MAX_PACKAGES),
  lineItems: z.array(lineItemInputSchema).max(200),
  years: z.array(yearInputSchema).min(MIN_HORIZON_YEARS).max(MAX_HORIZON_YEARS),
});

/**
 * The cross-field rules. Each one names the exact path it failed on, because the
 * editor turns `issue.path` into "scroll to this field and outline it".
 */
export const scenarioModelSchema = modelShape.superRefine((model, ctx) => {
  const packageIds = new Set(model.packages.map((pkg) => pkg.id));

  if (packageIds.size !== model.packages.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["packages"],
      message: "Two packages share an id.",
    });
  }

  if (model.years.length !== model.horizonYears) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["years"],
      message: `A ${model.horizonYears}-year horizon needs exactly ${model.horizonYears} year rows, not ${model.years.length}.`,
    });
  }

  model.years.forEach((year, index) => {
    if (year.yearIndex !== undefined && year.yearIndex !== index + 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["years", index, "yearIndex"],
        message: `Year rows must be in order; this one sits at position ${index + 1} but claims to be year ${year.yearIndex}.`,
      });
    }

    // A mix key that names no package would silently vanish on save, taking the
    // clients it allocated with it. Better to refuse it while it is still visible.
    for (const key of Object.keys(year.mix)) {
      if (!packageIds.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["years", index, "mix", key],
          message: "This mix entry does not name a package in this scenario.",
        });
      }
    }

    // Deliberately NOT checking that the mix sums to newClients. The old page
    // lets you sign 10 clients and allocate 8; the engine reports the other two
    // as UNASSIGNED_CLIENTS and earns nothing from them. See docs/06-api.md.
  });

  model.lineItems.forEach((item, index) => {
    if (item.startYear > model.horizonYears) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["lineItems", index, "startYear"],
        message: `This line starts in year ${item.startYear}, past the ${model.horizonYears}-year horizon.`,
      });
    }
    if (item.endYear !== null) {
      if (item.endYear < item.startYear) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lineItems", index, "endYear"],
          message: "A line cannot end before it starts.",
        });
      }
      if (item.endYear > model.horizonYears) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lineItems", index, "endYear"],
          message: `This line ends in year ${item.endYear}, past the ${model.horizonYears}-year horizon.`,
        });
      }
    }

    for (const id of item.packageIds) {
      if (!packageIds.has(id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lineItems", index, "packageIds"],
          message: "This line is restricted to a package that is not in this scenario.",
        });
      }
    }

    // PCT_OF_* items carry a percentage in the same `amount` column as everything
    // else, so the 0..100 bound can only be applied once the basis is known.
    if ((item.basis === "PCT_OF_REVENUE" || item.basis === "PCT_OF_COST") && item.amount > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["lineItems", index, "amount"],
        message: "A percentage basis takes a percentage, so at most 100.",
      });
    }
  });
});

export type ScenarioModelInput = z.infer<typeof scenarioModelSchema>;

/**
 * Drops the wire-only fields and fills in the currency, producing the exact
 * {@link ScenarioModel} the engine takes. One place does this so the server and
 * the editor cannot disagree about what a parsed payload means.
 */
export function toScenarioModel(input: ScenarioModelInput, currencyCode: string): ScenarioModel {
  return {
    currencyCode,
    horizonYears: input.horizonYears,
    commitmentYears: input.commitmentYears,
    discountRatePct: input.discountRatePct,
    reuseAssets: input.reuseAssets,
    packages: input.packages,
    lineItems: input.lineItems.map((item) => ({
      id: item.id,
      name: item.name,
      direction: item.direction,
      group: item.group,
      basis: item.basis,
      amount: item.amount,
      unitsPerClient: item.unitsPerClient,
      attachPct: item.attachPct,
      startYear: item.startYear,
      endYear: item.endYear,
      packageIds: item.packageIds,
      sortOrder: item.sortOrder,
    })),
    years: input.years.map((year) => ({
      newClients: year.newClients,
      mix: year.mix,
      churnMode: year.churnMode,
      churnCount: year.churnCount,
      churnRatePct: year.churnRatePct,
      ...(year.note === undefined ? {} : { note: year.note }),
    })),
  };
}

/* --------------------------------------------------------------- semantics */

export type ModelIssueCode = "DUPLICATE_PACKAGE_NAME" | "MODEL_INVALID";

export interface ModelIssue {
  code: ModelIssueCode;
  message: string;
  /** The same shape zod uses, so the editor can highlight either kind alike. */
  path: (string | number)[];
}

/**
 * The rules a model can break while still being a well-formed document.
 *
 * Package-name uniqueness lives here rather than in the schema above because it
 * has its own status code — `409 DUPLICATE_PACKAGE_NAME` — and because the
 * database enforces it too (`@@unique([scenarioId, name])`); catching it here
 * turns a constraint violation into a sentence naming the package.
 */
/**
 * Structurally minimal on purpose: the same rules have to run over a parsed
 * request body, an editor draft and a model rebuilt from an imported file, and
 * those three are the same object in every way these checks read.
 */
export interface SemanticCheckInput {
  packages: {
    name: string;
    billingModel: BillingModel;
    maintenanceMode: MaintenanceMode;
    oneTimePrice: number;
  }[];
}

export function checkModelSemantics(model: SemanticCheckInput): ModelIssue[] {
  const issues: ModelIssue[] = [];

  const seen = new Set<string>();
  model.packages.forEach((pkg, index) => {
    // Case-insensitive: two packages called "Starter" and "starter" in the same
    // picker is a mistake every time, even though Postgres would store both.
    const key = pkg.name.trim().toLowerCase();
    if (seen.has(key)) {
      issues.push({
        code: "DUPLICATE_PACKAGE_NAME",
        message: `Two packages are called "${pkg.name}". Package names must be unique within a scenario.`,
        path: ["packages", index, "name"],
      });
      return;
    }
    seen.add(key);
  });

  model.packages.forEach((pkg, index) => {
    // A perpetual package with no licence fee and no maintenance can never earn
    // anything. That is always a half-finished edit rather than an intent, so it
    // is refused rather than quietly projected as a flat zero.
    if (
      pkg.billingModel === "PERPETUAL" &&
      pkg.maintenanceMode === "NONE" &&
      pkg.oneTimePrice === 0
    ) {
      issues.push({
        code: "MODEL_INVALID",
        message: `"${pkg.name}" is perpetual with no licence fee and no maintenance, so it can never earn anything. Set a one-time price, or turn maintenance on.`,
        path: ["packages", index, "oneTimePrice"],
      });
    }
  });

  return issues;
}

/* --------------------------------------------------------------- summaries */

/** The engine's warnings, as they cross the wire. Mirrors `ProjectionWarning`. */
export const projectionWarningSchema = z.object({
  code: z.string(),
  message: z.string(),
  year: z.number().int().optional(),
  packageId: z.string().optional(),
  lineItemId: z.string().optional(),
});

/**
 * What a save hands back, and what a `ScenarioVersion` stores alongside its
 * snapshot: enough to render the KPI strip without recomputing, and nothing that
 * would make the row grow with the horizon.
 */
export const projectionSummarySchema = z.object({
  revenue: z.number(),
  cost: z.number(),
  net: z.number(),
  roiPct: z.number(),
  breakEvenYears: z.number().nullable(),
  npv: z.number().nullable(),
  endingActive: z.number().int(),
  endingInstalled: z.number().int(),
  endingArr: z.number(),
  warnings: z.array(projectionWarningSchema),
});
export type ProjectionSummary = z.infer<typeof projectionSummarySchema>;

/**
 * The denormalised copy on the `Scenario` row. Every field is nullable because a
 * scenario that has never been saved through the model endpoint has no figures
 * yet, and a list view must be able to say "not computed" rather than "0".
 */
export const scenarioSummarySchema = z.object({
  revenue: z.number().nullable(),
  cost: z.number().nullable(),
  net: z.number().nullable(),
  roiPct: z.number().nullable(),
  breakEvenYears: z.number().nullable(),
  endingArr: z.number().nullable(),
  endingActive: z.number().int().nullable(),
});
export type ScenarioSummary = z.infer<typeof scenarioSummarySchema>;

/* --------------------------------------------------------------- resources */

export const scenarioShareSchema = z.object({
  userId: z.string(),
  user: userRefSchema,
  permission: sharePermissionSchema,
  grantedById: z.string(),
  createdAt: isoDateTimeSchema,
});
export type ScenarioShareDto = z.infer<typeof scenarioShareSchema>;

/**
 * What the caller may do with this scenario, decided once on the server and sent
 * with the resource. The client uses it to disable controls; the server enforces
 * the same rules regardless. Sending it stops the two forming separate opinions
 * about who may press save.
 */
export const scenarioAbilitiesSchema = z.object({
  canEdit: z.boolean(),
  canShare: z.boolean(),
  canArchive: z.boolean(),
  canSetBaseline: z.boolean(),
  canDelete: z.boolean(),
});
export type ScenarioAbilities = z.infer<typeof scenarioAbilitiesSchema>;

/** A row in a scenario list: metadata and the denormalised KPIs, never the model. */
export const scenarioRowSchema = z.object({
  id: z.string(),
  productId: z.string(),
  authorId: z.string(),
  author: userRefSchema,
  name: z.string(),
  description: z.string().nullable(),
  currencyCode: z.string(),
  horizonYears: z.number().int(),
  commitmentYears: z.number().int(),
  discountRatePct: z.number(),
  reuseAssets: z.boolean(),
  visibility: visibilitySchema,
  isBaseline: z.boolean(),
  version: z.number().int(),
  summary: scenarioSummarySchema,
  packageCount: z.number().int(),
  shareCount: z.number().int(),
  abilities: scenarioAbilitiesSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  archivedAt: isoDateTimeSchema.nullable(),
});
export type ScenarioRow = z.infer<typeof scenarioRowSchema>;

/** A row plus the thing the editor actually loads. */
export const scenarioDetailSchema = scenarioRowSchema.extend({
  model: modelShape.extend({ currencyCode: currencyCodeSchema }),
  shares: z.array(scenarioShareSchema),
});
export type ScenarioDetail = z.infer<typeof scenarioDetailSchema>;

/* ---------------------------------------------------------------- requests */

export const createScenarioSchema = z.object({
  name: z.string().trim().min(1, "Give the scenario a name.").max(120),
  description: z.string().trim().max(1000).nullish(),
  /**
   * A scenario id to copy, or the literal "baseline" for whatever the product
   * currently points at. Absent means start from one empty package — see
   * `blankScenarioModel` in src/shared/roi/defaults.ts.
   */
  forkFrom: z.union([z.literal("baseline"), idSchema]).optional(),
  visibility: visibilitySchema.optional(),
});
export type CreateScenarioRequest = z.infer<typeof createScenarioSchema>;

export const patchScenarioSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1000).nullable(),
    visibility: visibilitySchema,
    isBaseline: z.boolean(),
  })
  .partial();
export type PatchScenarioRequest = z.infer<typeof patchScenarioSchema>;

export const duplicateScenarioSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
});

export const saveModelRequestSchema = z.object({
  /** The version the editor loaded. A mismatch means someone else saved first. */
  version: z.number().int().min(1),
  label: z.string().trim().max(80).optional(),
  model: scenarioModelSchema,
});
export type SaveModelRequest = z.infer<typeof saveModelRequestSchema>;

export const saveModelResponseSchema = z.object({
  scenario: scenarioDetailSchema,
  summary: projectionSummarySchema,
});
export type SaveModelResponse = z.infer<typeof saveModelResponseSchema>;

/** The 409 body: the client diffs its draft against `current` in the conflict dialog. */
export const versionConflictDetailsSchema = z.object({
  currentVersion: z.number().int(),
  current: scenarioDetailSchema,
});

export const createShareSchema = z.object({
  email: z.string().trim().toLowerCase().email("Enter a valid email address.").max(254),
  permission: sharePermissionSchema.default("VIEW"),
});
export type CreateShareRequest = z.infer<typeof createShareSchema>;

export const scenarioListQuerySchema = z.object({
  mine: booleanQuerySchema,
  visibility: visibilitySchema.optional(),
  q: searchQuerySchema,
  includeArchived: booleanQuerySchema,
});

/* ---------------------------------------------------------------- versions */

export const scenarioVersionRowSchema = z.object({
  id: z.string(),
  scenarioId: z.string(),
  version: z.number().int(),
  label: z.string().nullable(),
  summary: projectionSummarySchema.nullable(),
  createdById: z.string(),
  createdBy: userRefSchema,
  pinned: z.boolean(),
  createdAt: isoDateTimeSchema,
});
export type ScenarioVersionRow = z.infer<typeof scenarioVersionRowSchema>;

export const scenarioVersionDetailSchema = scenarioVersionRowSchema.extend({
  snapshot: modelShape.extend({ currencyCode: currencyCodeSchema }),
});
export type ScenarioVersionDetail = z.infer<typeof scenarioVersionDetailSchema>;

/** Oldest non-pinned snapshots beyond this are dropped on write — docs/03. */
export const MAX_VERSIONS_PER_SCENARIO = 50;

/* ------------------------------------------------------- export and import */

/**
 * The canonical document — the contract half of src/shared/roi/document.ts,
 * which is where the reasoning about what belongs in a file lives.
 *
 * `GET /api/scenarios/:id/export` returns exactly this. The importer accepts it
 * *and* the two other shapes `parseScenarioDocument` recognises, so the import
 * body is deliberately not validated against this schema — see
 * `importDocumentSchema` below.
 */
export const scenarioDocumentSchema = z.object({
  schema: z.literal(EXPORT_SCHEMA),
  version: z.literal(EXPORT_VERSION),
  exportedAt: isoDateTimeSchema,
  product: z.object({ name: z.string(), currencyCode: currencyCodeSchema }),
  scenario: z.object({
    name: z.string(),
    description: z.string().nullable(),
    sourceId: z.string().nullable(),
    sourceVersion: z.number().int().nullable(),
  }),
  model: modelShape.extend({ currencyCode: currencyCodeSchema }),
});
export type ScenarioDocumentDto = z.infer<typeof scenarioDocumentSchema>;

/**
 * Any JSON object. The import endpoint has to accept a legacy `znms-roi-scenario`
 * file and a bare snapshot as well as a canonical document, so recognising the
 * shape is `parseScenarioDocument`'s job and rejecting a non-object is this
 * schema's whole contribution. Validating harder here would turn "this isn't an
 * ROI scenario" into a wall of zod paths.
 */
export const importDocumentSchema = z.record(z.unknown());

/* --------------------------------------------------------------- compare */

export const compareRequestSchema = z.object({
  ids: z
    .array(idSchema)
    .min(MIN_COMPARE_SCENARIOS, "Pick at least two scenarios to compare.")
    .max(MAX_COMPARE_SCENARIOS, `A comparison holds at most ${MAX_COMPARE_SCENARIOS} scenarios.`)
    // Comparing a scenario with itself is a request the UI cannot produce and
    // that would draw two identical lines; de-duplicating is kinder than a 400.
    .transform((ids) => [...new Set(ids)]),
});
export type CompareRequest = z.input<typeof compareRequestSchema>;

/**
 * One year of one scenario, aligned by year index across the comparison. Only
 * the series the compare chart and matrix draw — not the full `YearResult`,
 * which is forty fields and would make a four-way comparison the heaviest
 * response in the app.
 */
export const compareYearSchema = z.object({
  year: z.number().int(),
  revenue: z.number(),
  cost: z.number(),
  net: z.number(),
  cumRevenue: z.number(),
  cumCost: z.number(),
  cumNet: z.number(),
  activeTotal: z.number().int(),
});
export type CompareYear = z.infer<typeof compareYearSchema>;

export const compareEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  productId: z.string(),
  productName: z.string(),
  productSlug: z.string(),
  currencyCode: z.string(),
  horizonYears: z.number().int(),
  commitmentYears: z.number().int(),
  version: z.number().int(),
  isBaseline: z.boolean(),
  author: userRefSchema,
  updatedAt: isoDateTimeSchema,
  /** Recomputed for the comparison, never read from the denormalised columns. */
  summary: projectionSummarySchema,
  series: z.array(compareYearSchema),
  /**
   * The inputs, so the screen can show *why* two scenarios differ rather than
   * only that they do. `diffModels` runs in the browser over these; asking the
   * server to diff instead would mean a second diff implementation for the
   * version screen, which runs against a snapshot the server never sees twice.
   */
  model: modelShape.extend({ currencyCode: currencyCodeSchema }),
});
export type CompareEntry = z.infer<typeof compareEntrySchema>;

export const compareResponseSchema = z.object({
  /** In the order they were asked for, so the columns match the URL. */
  scenarios: z.array(compareEntrySchema),
  /**
   * True when the scenarios do not all price in the same currency. The figures
   * are still returned — the shapes are comparable even when the totals are not
   * — and the screen says so rather than quietly adding dollars to rufiyaa.
   */
  currencyMismatch: z.boolean(),
});
export type CompareResponse = z.infer<typeof compareResponseSchema>;
